import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_CONFIG } from '../shared/constants/api';
import { useTranslation } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';
import logo from '../logo.svg';
import './Auth.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();

  // Google Sign In Script yükle
  useEffect(() => {
    const googleScript = document.createElement('script');
    googleScript.src = 'https://accounts.google.com/gsi/client';
    googleScript.async = true;
    googleScript.defer = true;
    document.body.appendChild(googleScript);

    window.handleGoogleSignIn = handleGoogleSignIn;

    // Apple Sign In Script yükle
    const appleScript = document.createElement('script');
    appleScript.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    appleScript.async = true;
    appleScript.defer = true;
    document.body.appendChild(appleScript);

    appleScript.onload = () => {
      window.AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.monnaify.web',
        scope: 'name email',
        redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || 'https://supportive-grace-production-0491.up.railway.app',
        state: 'origin:web',
        usePopup: true,
      });
    };

    return () => {
      document.body.removeChild(googleScript);
      document.body.removeChild(appleScript);
      delete window.handleGoogleSignIn;
    };
  }, []);

  const handleAppleLoginClick = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await window.AppleID.auth.signIn();
      await handleAppleSignIn(response);
    } catch (err) {
      console.error('Apple Sign In Cancelled or Failed:', err);
      if (err.error !== 'popup_closed_by_user') {
        setError(t('auth.login.appleFailed'));
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleGoogleSignIn = async (response) => {
    console.log('🔵 handleGoogleSignIn called with response:', response);
    try {
      setLoading(true);
      setError('');

      console.log('🔵 Sending request to backend...');
      const res = await fetch(`${API_CONFIG.BASE_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idToken: response.credential,
          googleId: response.clientId,
          email: response.email,
          name: response.name,
        }),
      });

      console.log('🔵 Backend response status:', res.status);

      if (res.ok) {
        const data = await res.json();
        console.log('✅ Backend data:', data);
        console.log('🔵 Saving to localStorage...');
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        console.log('✅ Saved. Redirecting to /dashboard...');
        // Hard navigation ile sayfayı yeniden yükle (AuthContext için)
        window.location.href = '/dashboard';
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('❌ Backend error:', errorData);
        setError(errorData.error || t('auth.login.googleFailed'));
      }
    } catch (err) {
      console.error('❌ Exception:', err);
      setError(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async (response) => {
    try {
      setLoading(true);
      setError('');

      console.log('🍎 Apple Sign In Response:', response);

      const res = await fetch(`${API_CONFIG.BASE_URL}/auth/apple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identityToken: response.authorization.id_token,
          user: response.authorization.user,
          email: response.authorization.user?.email || response.user?.email,
          fullName: response.authorization.user?.name || response.user?.name,
        }),
      });

      console.log('🍎 Backend Response Status:', res.status);

      if (res.ok) {
        const data = await res.json();
        console.log('✅ Apple Login Success:', data);
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.location.href = '/dashboard';
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('❌ Backend Error:', errorData);
        setError(errorData.error || t('auth.login.appleFailed'));
      }
    } catch (err) {
      console.error('❌ Exception:', err);
      setError(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher />
      </div>
      <div className="auth-box">
        <img src={logo} alt="Monnaify Logo" className="auth-logo" />
        <h1>{t('auth.login.title')}</h1>
        <p className="auth-subtitle">{t('auth.login.subtitle')}</p>
        <p className="auth-description">{t('auth.login.description')}</p>

        {error && (
          <div className="error-message">
            {error}
            {error.toLowerCase().includes('sosyal') || error.toLowerCase().includes('google') || error.toLowerCase().includes('apple') ? (
              <div style={{ marginTop: 8 }}>
                <Link to="/forgot-password" style={{ color: '#c33', fontWeight: 600 }}>
                  {t('auth.login.setPasswordLink')}
                </Link>
              </div>
            ) : null}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('auth.login.email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('auth.login.emailPlaceholder')}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.login.password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              disabled={loading}
              minLength={6}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('auth.login.loading') : t('auth.login.submit')}
          </button>
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <Link to="/forgot-password" style={{ fontSize: 13, color: '#667eea', textDecoration: 'none' }}>
              {t('auth.login.forgotPassword')}
            </Link>
          </div>
        </form>

        <div className="divider">
          <span>{t('auth.login.or')}</span>
        </div>

        {/* Google Sign In Button */}
        <div
          id="g_id_onload"
          data-client_id="261119402074-svolkhsshoe6fl3le4gd41l84ph9ecn8.apps.googleusercontent.com"
          data-callback="handleGoogleSignIn"
          data-auto_prompt="false">
        </div>
        <div className="google-signin-wrapper">
          <div
            className="g_id_signin"
            data-type="standard"
            data-size="large"
            data-theme="outline"
            data-text="signin_with"
            data-shape="rectangular"
            data-logo_alignment="left"
            data-width="340">
          </div>
        </div>

        {/* Apple Sign In Button */}
        <button
          type="button"
          className="oauth-button apple-oauth-button"
          onClick={handleAppleLoginClick}
          disabled={loading}>
          <svg className="oauth-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="currentColor"/>
          </svg>
          <span>{t('auth.login.continueWithApple')}</span>
        </button>

        <p className="auth-link">
          {t('auth.login.noAccount')} <Link to="/register">{t('auth.login.register')}</Link>
        </p>
        <div className="auth-footer-links">
          <Link to="/privacy">
            {t('auth.login.privacyPolicy')}
          </Link>
        </div>
      </div>
    </div>
  );
}

// Google callback fonksiyonunu window'a ekle
window.handleGoogleSignIn = (response) => {
  // Bu fonksiyon Google button'dan çağrılacak
  console.log('Google Sign In response:', response);
};

export default Login;
